<template>
  <div
    ref="editorRoot"
    class="grid-page-chart-editor"
  >
    <div :class="['_top', desktopSidebarHidden ? '-sidebar-hidden' : '']">
      <div class="_left">
        <breadcrumbs
          class="_breadcrumbs"
          :breadcrumbs="breadcrumbItems"
        />
        <div class="_divider">
          /
        </div>
        <m-dropdown
          :title="$t('general.actions')"
          class="_title"
        >
          <m-btn
            type="clickable"
            hide-border
            small
          >
            {{ maxLength(props.gridPageTile.title, 30) }}
          </m-btn>
          <template #overlay>
            <m-card
              padding-xxs
              :style="{ width: '20rem'}"
            >
              <m-focusable>
                <simple-editor
                  auto-focus
                  :initial-value="props.gridPageTile.title"
                  :read-only="false"
                  auto-select
                  :placeholder="$t('gridPageChartEditor.titlePlaceholder')"
                  @update:value="updateTitle"
                />
              </m-focusable>
            </m-card>
          </template>
        </m-dropdown>
        <m-dropdown
          v-model:value="descriptionEditMode"
          :style="{ marginLeft: '.6rem' }"
          :title="$t('general.actions')"
          class="_description"
        >
          <m-btn
            type="clickable"
            small
            light
            :icon="props.gridPageTile.description === '' ? 'plus' : undefined"
            hide-border
          >
            <template v-if="props.gridPageTile.description !== ''">
              {{ maxLength(props.gridPageTile.description, 50) }}
            </template>
            <template v-else>
              {{ $t('gridPageChartEditor.addDescription') }}
            </template>
          </m-btn>
          <template #overlay>
            <m-card
              padding-xxs
              :style="{ width: '30rem'}"
            >
              <m-focusable>
                <simple-editor
                  :style="{ width: '100%' }"
                  :initial-value="props.gridPageTile.description"
                  :read-only="false"
                  auto-select
                  auto-focus
                  :placeholder="$t('gridPageChartEditor.descriptionPlaceholder')"
                  :enter-handler="updateDescription"
                  @update:value="updateDescription"
                  @enter="descriptionEditMode = false"
                />
              </m-focusable>
            </m-card>
          </template>
        </m-dropdown>
      </div>
      <div class="_right">
        <template
          v-if="isDirty"
        >
          <m-btn
            class="_discard-btn"
            hide-border
            light
            @click="discard"
          >
            {{ $t('general.discardChanges') }}
          </m-btn>
          <m-btn
            color="primary"
            @click="save"
          >
            {{ $t('general.save') }}
          </m-btn>
        </template>
        <m-btn
          hide-border
          light
          icon="close"
          fab
          @click="handleClose"
        />
      </div>
    </div>
    <div class="_bottom">
      <div class="_left">
        <div
          v-if="chartDataQuery.loading.value"
          class="_loading-overlay"
        >
          <m-spinner />
        </div>
        <component
          :is="[gridPageChartType.table, gridPageChartType.metric].includes(chartType) ? 'div' : DraggableLayout"
          v-model:position="tablePosition"
          :class="['_draggable-layout', { '-table': chartType === gridPageChartType.table }]"
        >
          <ChartDataLimitHint
            v-if="chartType === gridPageChartType.table"
            :chart-data-result="chartDataQuery.result.value"
            class="_data-limit-hint"
          />
          <template
            v-if="chartDataQuery.result.value?.isTruncated"
            #hint
          >
            <ChartDataLimitHint
              :chart-data-result="chartDataQuery.result.value"
              class="_data-limit-hint"
            />
          </template>
          <div
            :class="['_chart-container', { '-table': chartType === gridPageChartType.table }]"
          >
            <m-content
              class="_chart-content"
              :padding-x="12"
              :padding-top="10"
              :padding-bottom="10"
            >
              <grid-page-chart
                :key="settingsWidth"
                :chart="toSave"
                :chart-data-query="chartDataQuery"
                size="large"
                :id-prefix="`chart_editor_${props.gridPageTile.uid}`"
              />
            </m-content>
          </div>
          <template #bottom>
            <m-content
              :padding-x="12"
              :padding-top="10"
              :padding-bottom="10"
              class="_table-container"
            >
              <grid-page-chart-data-table
                :chart="toSave"
                :chart-data-query="chartDataQuery"
              />
            </m-content>
          </template>
        </component>
      </div>
      <div
        class="_right"
        :style="isMobile ? {} : { width: `${settingsWidth}px` }"
      >
        <div
          :class="['_resize-handle', { '-dragging': resizing }]"
          @mousedown="handleResizeStart"
        />
        <div class="_time-mode-tabs">
          <div
            v-for="option in timeModeOptions"
            :key="option.value"
            class="_time-mode-tab"
          >
            <m-tab
              :title="option.text"
              :active="timeMode === option.value"
              @click="timeMode = option.value"
            />
          </div>
        </div>
        <div class="_inner">
          <div class="_section-header">
            {{ $t('gridPageChartEditor.title') }}
          </div>
          <m-select
            :items="sources"
            :value="source"
            :disabled="isTimeSeriesMode"
            value-key="value"
            item-text="text"
            placement="bottomRight"
            full-width
            @input="val => source = val"
          >
            <template #trigger>
              <m-card-item
                icon="database"
                :disabled="isTimeSeriesMode"
              >
                {{ $t('gridPageChartEditor.source') }}
                <template #right>
                  <chart-editor-setting-value :value="sourceDisplayValue" />
                </template>
              </m-card-item>
            </template>
          </m-select>
          <m-dropdown
            block
            placement="bottomRight"
            :relocate-key="filter"
            :title="$t('gridPageChartEditor.filter')"
          >
            <m-card-item icon="funnel">
              {{ $t('gridPageChartEditor.filter') }}
              <template #right>
                <chart-editor-setting-value :value="filter.children.length > 0 ? filter.children.length : ''" />
              </template>
            </m-card-item>
            <template #overlay>
              <m-card padding-xs>
                <filter-chips
                  :style="{ minWidth: '30rem' }"
                  :props="filterProps"
                  :filter="filter"
                  :quick-filters="quickFilters"
                  :max-tags="2"
                  has-dynamic-dates
                  layout="vertical"
                  @update:filter="filter = $event"
                  @update:quick-filters="quickFilters = $event"
                />
              </m-card>
            </template>
          </m-dropdown>
          <goal-cycle-selector
            v-if="source === goalConfig.model"
            v-model:value="selectedCycles"
            keep-open-on-click
            placement="bottomRight"
            multiple
            full-width
          >
            <template #trigger="{ value: cycles }">
              <m-card-item icon="retweet">
                {{ $t('gridPageChartEditor.cycle') }}
                <template #right>
                  <chart-editor-setting-value :value="cycles.length === 1 ? cycles[0].title : cycles.length > 1 ? $t('gridPageChartEditor.cycleCount', { count: cycles.length }) : ''" />
                </template>
              </m-card-item>
            </template>
          </goal-cycle-selector>
          <div class="_section-header">
            {{ $t('gridPageChartEditor.chart') }}
          </div>
          <div class="_chart-type-selector">
            <m-tooltip
              v-for="type in visibleChartTypes"
              :key="type"
            >
              <m-focusable
                type="clickable"
                :class="{ '_chart-btn': true, '-selected': chartType === type }"
                :hide-hover="chartType === type"
                @click="chartType = type"
              >
                <div class="_icon-wrapper">
                  <m-icon
                    :type="getIconByType(type)"
                    size="24"
                    :color="chartType === type ? $colors.blue.base : $colors.grey.lighten1"
                  />
                </div>
              </m-focusable>
              <template #title>
                {{ $t(`gridPageChartEditor.${type}`) }}
              </template>
            </m-tooltip>
          </div>
          <!-- X axis / Y axis sections for bar, column, line, table -->
          <template v-if="[gridPageChartType.bar, gridPageChartType.column, gridPageChartType.line, gridPageChartType.table].includes(chartType)">
            <!-- X axis (always first) -->
            <div class="_section-header">
              {{ chartType === gridPageChartType.table ? $t('gridPageChartEditor.columns') : $t('gridPageChartEditor.xAxis') }}
            </div>
            <template v-if="categoryIsXAxis">
              <!-- Category content on X axis (column, line, table) -->
              <template v-if="isTimeSeriesMode">
                <m-select
                  :items="timeSeriesIntervalOptions"
                  :value="selectedInterval"
                  value-key="value"
                  item-text="text"
                  placement="bottomRight"
                  full-width
                  @input="val => selectedInterval = val"
                >
                  <template #trigger>
                    <m-card-item icon="calendar">
                      {{ $t('gridPageChartEditor.interval') }}
                      <template #right>
                        <chart-editor-setting-value :value="intervalDisplayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </m-select>
              </template>
              <template v-else>
                <group-by-selector
                  :value="xAxisGroupBy"
                  :properties="properties"
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                  @update:value="setXAxisGroupBy"
                >
                  <template #trigger="{ displayValue, isOrphan }">
                    <chart-editor-setting-row
                      icon="corner-down-right"
                      :display-value="displayValue"
                      :is-orphan="isOrphan"
                    >
                      {{ $t('gridPageChartEditor.whatToShow') }}
                    </chart-editor-setting-row>
                  </template>
                </group-by-selector>
                <bucket-selector
                  v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
                  v-model:value="customBucket"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.bucket') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </bucket-selector>
                <chart-editor-toggle-row
                  v-if="showExcludeNullValues"
                  icon="eye-invisible"
                  :label="$t('gridPageChartEditor.excludeNullValues')"
                  :value="excludeNullValues"
                  @toggle="toggleExcludeNullValues"
                />
                <sort-selector
                  :show="showXAxisSort"
                  :options="xAxisSortOptions"
                  :value="xAxisSortBy"
                  :groups="xAxisGroups"
                  :manual-order="xAxisManualOrder"
                  :hidden-groups="xAxisHiddenGroups"
                  @update:value="val => xAxisSortBy = val"
                  @update:manual-order="val => xAxisManualOrder = val"
                  @update:hidden-groups="val => xAxisHiddenGroups = val"
                />
              </template>
            </template>
            <template v-else>
              <!-- Metric content on X axis (bar) -->
              <aggregation-selector
                :value="yAxisGroupBy"
                :properties="properties"
                @update:value="setYAxisGroupBy"
              >
                <template #trigger="{ displayValue, isOrphan }">
                  <chart-editor-setting-row
                    icon="corner-down-right"
                    :display-value="displayValue"
                    :is-orphan="isOrphan"
                  >
                    {{ $t('gridPageChartEditor.whatToShow') }}
                  </chart-editor-setting-row>
                </template>
              </aggregation-selector>
              <chart-editor-toggle-row
                v-if="showBoundedProgress"
                icon="toggle-top"
                :label="$t('gridPageChartEditor.boundedProgress')"
                :tooltip="$t('gridPageChartEditor.boundedProgressTooltip')"
                :value="boundedProgress"
                @toggle="toggleBoundedProgress"
              />
              <template v-if="!isTimeSeriesMode">
                <group-by-selector
                  v-model:value="stackOptions"
                  :properties="properties"
                  show-none
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                >
                  <template #trigger="{ displayValue, isOrphan }">
                    <chart-editor-setting-row
                      icon="toggle-center"
                      :display-value="displayValue"
                      :is-orphan="isOrphan"
                    >
                      {{ $t('gridPageChartEditor.groupBy') }}
                    </chart-editor-setting-row>
                  </template>
                </group-by-selector>
                <bucket-selector
                  v-if="stackOptions.groupBy === chartGroupByOption.bucket"
                  v-model:value="stackCustomBucket"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.bucket') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </bucket-selector>
                <chart-editor-toggle-row
                  v-if="showStacked"
                  icon="bar-chart"
                  :label="$t('gridPageChartEditor.stackSeries')"
                  :value="stacked"
                  @toggle="toggleStacked"
                />
                <sort-selector
                  :show="showStackSort"
                  :options="stackSortOptions"
                  :value="stackSortBy"
                  :groups="stackGroups"
                  :manual-order="stackManualOrder"
                  :hidden-groups="stackHiddenGroups"
                  :groups-label="$t('gridPageChartEditor.series')"
                  @update:value="val => stackSortBy = val"
                  @update:manual-order="val => stackManualOrder = val"
                  @update:hidden-groups="val => stackHiddenGroups = val"
                />
              </template>
              <reference-lines-selector
                v-if="showReferenceLines"
                :reference-lines="referenceLines"
                :style-options="referenceLineStyleOptions"
                @add="addReferenceLine"
                @remove="removeReferenceLine"
              />
              <precision-selector
                v-if="showPrecision"
                :value="precision"
                @update:value="val => precision = val"
              />
            </template>

            <!-- Y axis (always second) -->
            <div class="_section-header">
              {{ chartType === gridPageChartType.table ? $t('gridPageChartEditor.values') : $t('gridPageChartEditor.yAxis') }}
            </div>
            <template v-if="categoryIsXAxis">
              <!-- Metric content on Y axis (column, line, table) -->
              <template v-if="isTimeSeriesMode">
                <m-select
                  :items="timeSeriesMetricOptions"
                  :value="selectedTimeSeriesMetricKey"
                  value-key="key"
                  item-text="text"
                  match-trigger-width
                  has-tooltip
                  tooltip-placement="left"
                  full-width
                  placement="bottomRight"
                  @input="selectTimeSeriesMetric"
                >
                  <template #trigger>
                    <m-card-item icon="corner-left-up">
                      {{ $t('gridPageChartEditor.whatToShow') }}
                      <template #right>
                        <chart-editor-setting-value :value="timeSeriesMetricDisplayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </m-select>
                <chart-editor-toggle-row
                  v-if="showBoundedProgress"
                  icon="toggle-top"
                  :label="$t('gridPageChartEditor.boundedProgress')"
                  :tooltip="$t('gridPageChartEditor.boundedProgressTooltip')"
                  :value="boundedProgress"
                  @toggle="toggleBoundedProgress"
                />
              </template>
              <template v-else>
                <aggregation-selector
                  :value="yAxisGroupBy"
                  :properties="properties"
                  @update:value="setYAxisGroupBy"
                >
                  <template #trigger="{ displayValue, isOrphan }">
                    <chart-editor-setting-row
                      icon="corner-left-up"
                      :display-value="displayValue"
                      :is-orphan="isOrphan"
                    >
                      {{ $t('gridPageChartEditor.whatToShow') }}
                    </chart-editor-setting-row>
                  </template>
                </aggregation-selector>
                <chart-editor-toggle-row
                  v-if="showBoundedProgress"
                  icon="toggle-top"
                  :label="$t('gridPageChartEditor.boundedProgress')"
                  :tooltip="$t('gridPageChartEditor.boundedProgressTooltip')"
                  :value="boundedProgress"
                  @toggle="toggleBoundedProgress"
                />
                <group-by-selector
                  v-model:value="stackOptions"
                  :properties="properties"
                  show-none
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                >
                  <template #trigger="{ displayValue, isOrphan }">
                    <chart-editor-setting-row
                      icon="toggle-center"
                      :display-value="displayValue"
                      :is-orphan="isOrphan"
                    >
                      {{ $t('gridPageChartEditor.groupBy') }}
                    </chart-editor-setting-row>
                  </template>
                </group-by-selector>
                <bucket-selector
                  v-if="stackOptions.groupBy === chartGroupByOption.bucket"
                  v-model:value="stackCustomBucket"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.bucket') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </bucket-selector>
                <chart-editor-toggle-row
                  v-if="showStacked"
                  icon="bar-chart"
                  :label="$t('gridPageChartEditor.stackSeries')"
                  :value="stacked"
                  @toggle="toggleStacked"
                />
                <sort-selector
                  :show="showStackSort"
                  :options="stackSortOptions"
                  :value="stackSortBy"
                  :groups="stackGroups"
                  :manual-order="stackManualOrder"
                  :hidden-groups="stackHiddenGroups"
                  :groups-label="$t('gridPageChartEditor.series')"
                  @update:value="val => stackSortBy = val"
                  @update:manual-order="val => stackManualOrder = val"
                  @update:hidden-groups="val => stackHiddenGroups = val"
                />
              </template>
              <reference-lines-selector
                v-if="showReferenceLines"
                :reference-lines="referenceLines"
                :style-options="referenceLineStyleOptions"
                @add="addReferenceLine"
                @remove="removeReferenceLine"
              />
              <precision-selector
                v-if="showPrecision"
                :value="precision"
                @update:value="val => precision = val"
              />
            </template>
            <template v-else>
              <!-- Category content on Y axis (bar) -->
              <group-by-selector
                :value="xAxisGroupBy"
                :properties="properties"
                :hidden-group-by-options="[chartGroupByOption.unique]"
                @update:value="setXAxisGroupBy"
              >
                <template #trigger="{ displayValue, isOrphan }">
                  <chart-editor-setting-row
                    icon="corner-left-up"
                    :display-value="displayValue"
                    :is-orphan="isOrphan"
                  >
                    {{ $t('gridPageChartEditor.whatToShow') }}
                  </chart-editor-setting-row>
                </template>
              </group-by-selector>
              <bucket-selector
                v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
                v-model:value="customBucket"
              >
                <template #trigger="{ displayValue }">
                  <m-card-item icon="toggle-center">
                    {{ $t('gridPageChartEditor.bucket') }}
                    <template #right>
                      <chart-editor-setting-value :value="displayValue" />
                    </template>
                  </m-card-item>
                </template>
              </bucket-selector>
              <chart-editor-toggle-row
                v-if="showExcludeNullValues"
                icon="eye-invisible"
                :label="$t('gridPageChartEditor.excludeNullValues')"
                :value="excludeNullValues"
                @toggle="toggleExcludeNullValues"
              />
              <sort-selector
                :show="showXAxisSort"
                :options="xAxisSortOptions"
                :value="xAxisSortBy"
                :groups="xAxisGroups"
                :manual-order="xAxisManualOrder"
                :hidden-groups="xAxisHiddenGroups"
                @update:value="val => xAxisSortBy = val"
                @update:manual-order="val => xAxisManualOrder = val"
                @update:hidden-groups="val => xAxisHiddenGroups = val"
              />
            </template>
          </template>
          <template v-if="chartType === gridPageChartType.pie">
            <div class="_section-header">
              {{ $t('gridPageChartEditor.data') }}
            </div>
            <group-by-selector
              v-model:value="xAxisGroupBy"
              :properties="properties"
              :hidden-group-by-options="[chartGroupByOption.unique]"
            >
              <template #trigger="{ displayValue, isOrphan }">
                <chart-editor-setting-row
                  icon="corner-down-right"
                  :display-value="displayValue"
                  :is-orphan="isOrphan"
                >
                  {{ $t('gridPageChartEditor.whatToShow') }}
                </chart-editor-setting-row>
              </template>
            </group-by-selector>
            <bucket-selector
              v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
              v-model:value="customBucket"
            >
              <template #trigger="{ displayValue }">
                <m-card-item icon="toggle-center">
                  {{ $t('gridPageChartEditor.bucket') }}
                  <template #right>
                    <chart-editor-setting-value :value="displayValue" />
                  </template>
                </m-card-item>
              </template>
            </bucket-selector>
            <chart-editor-toggle-row
              v-if="showExcludeNullValues"
              icon="eye-invisible"
              :label="$t('gridPageChartEditor.excludeNullValues')"
              :value="excludeNullValues"
              @toggle="toggleExcludeNullValues"
            />
            <aggregation-selector
              :value="yAxisGroupBy"
              :properties="properties"
              :show-time-series-options="source === goalConfig.model"
              @update:value="setYAxisAggregation"
            >
              <template #trigger="{ displayValue, isOrphan }">
                <chart-editor-setting-row
                  icon="corner-left-up"
                  :display-value="displayValue"
                  :is-orphan="isOrphan"
                >
                  {{ $t('gridPageChartEditor.representation') }}
                </chart-editor-setting-row>
              </template>
            </aggregation-selector>
            <chart-editor-toggle-row
              v-if="showBoundedProgress"
              icon="toggle-top"
              :label="$t('gridPageChartEditor.boundedProgress')"
              :tooltip="$t('gridPageChartEditor.boundedProgressTooltip')"
              :value="boundedProgress"
              @toggle="toggleBoundedProgress"
            />
            <precision-selector
              v-if="showPrecision"
              :value="precision"
              @update:value="val => precision = val"
            />
            <sort-selector
              :show="showXAxisSort"
              :options="xAxisSortOptions"
              :value="xAxisSortBy"
              :groups="xAxisGroups"
              :manual-order="xAxisManualOrder"
              :hidden-groups="xAxisHiddenGroups"
              @update:value="val => xAxisSortBy = val"
              @update:manual-order="val => xAxisManualOrder = val"
              @update:hidden-groups="val => xAxisHiddenGroups = val"
            />
          </template>
          <template v-if="chartType === gridPageChartType.metric">
            <div class="_section-header">
              {{ $t('gridPageChartEditor.data') }}
            </div>
            <aggregation-selector
              :value="yAxisGroupBy"
              :properties="properties"
              :show-time-series-options="source === goalConfig.model"
              @update:value="setYAxisAggregation"
            >
              <template #trigger="{ displayValue, isOrphan }">
                <chart-editor-setting-row
                  icon="corner-down-right"
                  :display-value="displayValue"
                  :is-orphan="isOrphan"
                >
                  {{ $t('gridPageChartEditor.whatToShow') }}
                </chart-editor-setting-row>
              </template>
            </aggregation-selector>
            <chart-editor-toggle-row
              v-if="showBoundedProgress"
              icon="toggle-top"
              :label="$t('gridPageChartEditor.boundedProgress')"
              :tooltip="$t('gridPageChartEditor.boundedProgressTooltip')"
              :value="boundedProgress"
              @toggle="toggleBoundedProgress"
            />
            <precision-selector
              v-if="showPrecision"
              :value="precision"
              @update:value="val => precision = val"
            />
            <chart-editor-toggle-row
              icon="font-size"
              :label="$t('gridPageChartEditor.metricTitle')"
              :value="metricTitleEnabled"
              @toggle="toggleMetricTitleEnabled"
            />
            <div
              v-if="metricTitleEnabled"
              class="_metric-title-input-row"
            >
              <m-text-field
                :value="metricTitle"
                :placeholder="$t('gridPageChartEditor.metricTitlePlaceholder')"
                full-width
                @update:value="updateMetricTitle"
              />
            </div>
            <div class="_section-header">
              {{ $t('gridPageChartEditor.style') }}
            </div>
            <m-dropdown
              block
              :title="$t('metricColorEditor.color')"
              placement="bottomRight"
            >
              <m-card-item icon="palette">
                {{ $t('metricColorEditor.color') }}
                <template #right>
                  <chart-editor-setting-value :value="metricColorLabel" />
                </template>
              </m-card-item>
              <template #overlay>
                <metric-color-editor
                  :color="metricColor"
                  :coloring-rules="metricColoringRules"
                  :coloring-mode="metricColoringMode"
                  @update:color="metricColor = $event"
                  @update:coloring-rules="onUpdateColoringRules"
                  @update:coloring-mode="metricColoringMode = $event"
                />
              </template>
            </m-dropdown>
          </template>
          <template v-if="chartType !== gridPageChartType.metric && chartType !== gridPageChartType.table && !isTimeSeriesMode">
            <div class="_section-header">
              {{ $t('gridPageChartEditor.style') }}
            </div>
            <m-dropdown
              block
              placement="bottomRight"
              :title="$t('gridPageChartEditor.colorScheme')"
            >
              <m-card-item icon="palette">
                {{ $t('gridPageChartEditor.colorScheme') }}
                <template #right>
                  <chart-editor-setting-value :value="colorSchemeOptionsMap[colorScheme]?.text" />
                </template>
              </m-card-item>
              <template #overlay>
                <m-card
                  no-padding
                  list
                >
                  <m-card-item
                    v-for="option in colorSchemeOptions"
                    :key="option.value"
                    @click="colorScheme = option.value"
                  >
                    <div class="_color-scheme-item">
                      <span class="_color-scheme-label">{{ option.text }}</span>
                      <span
                        v-if="option.swatches.length"
                        class="_color-scheme-swatches"
                      >
                        <span
                          v-for="(swatch, i) in option.swatches"
                          :key="i"
                          class="_color-swatch"
                          :style="{ backgroundColor: swatch }"
                        />
                      </span>
                    </div>
                  </m-card-item>
                </m-card>
              </template>
            </m-dropdown>
          </template>
        </div>
      </div>
    </div>
    <div class="_controls">
      <app-feedback-dropdown
        topic="customCharts"
        class="_feedback-btn"
      />
    </div>
  </div>
</template>

<script setup>
import AggregationSelector from '@/components/custom-grid/AggregationSelector.vue';
import AppFeedbackDropdown from '@/components/app-feedback/AppFeedbackDropdown.vue';
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs.vue';
import BucketSelector from '@/components/custom-grid/BucketSelector.vue';
import ChartDataLimitHint from '@/components/custom-grid/chart/ChartDataLimitHint.vue';
import ChartEditorSettingRow from '@/components/custom-grid/ChartEditorSettingRow.vue';
import ChartEditorSettingValue from '@/components/custom-grid/ChartEditorSettingValue.vue';
import ChartEditorToggleRow from '@/components/custom-grid/ChartEditorToggleRow.vue';
import DraggableLayout from '@/components/custom-grid/chart/DraggableLayout.vue';
import FilterChips from '@/components/filter/FilterChips.vue';
import GoalCycleSelector from '@/components/goal/cycle/GoalCycleSelector.vue';
import GridPageChart from '@/components/custom-grid/chart/GridPageChart.vue';
import GridPageChartDataTable from '@/components/custom-grid/GridPageChartDataTable.vue';
import GroupBySelector from '@/components/custom-grid/GroupBySelector.vue';
import MetricColorEditor from '@/components/custom-grid/MetricColorEditor.vue';
import PrecisionSelector from '@/components/custom-grid/PrecisionSelector.vue';
import ReferenceLinesSelector from '@/components/custom-grid/ReferenceLinesSelector.vue';
import SimpleEditor from '@/components/SimpleEditor.vue';
import SortSelector from '@/components/custom-grid/SortSelector.vue';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useChartDataQuery from '@/composables/chart-data-query';
import useChartProps from '@/composables/grid-page/chart/chart-props';
import useChartSort from '@/composables/chart-sort';
import useCleanFilter from '@/composables/saved-views/clean-filter';
import useConfirmDialog from '@/composables/confirm-dialog';
import useDebounce from 'shared/composables/debounce';
import useDragResize from 'shared/composables/drag-resize';
import useEventBus from '@/composables/event-bus';
import useGoalProperty from '@/composables/property/goal-property';
import useGoals from '@/composables/goal/goals';
import useGridPage from '@/composables/grid-page/grid-page';
import useGridPageTileChart from '@/composables/grid-page/chart/grid-page-tile-chart';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useParamDefaulter from '@/composables/view-params/default';
import useParamEnricher from '@/composables/view-params/enrich';
import useProperties from '@/composables/property/property';
import useResourceSettings from '@/composables/logged-in-user-account/resource-settings';
import useSnackbar from '@/composables/snackbar';
import useSpaces from '@/composables/space/spaces';
import useUsers from '@/composables/user/users';
import { SIDEBAR_SERVICE, TIME_SERIES_AGGREGATORS, VIEW_EVENT_BUS } from '@/lib/constants';
import { aggregatorType, chartGroupByOption, chartSortBy, coloringMode, gridPageChartColorScheme, gridPageChartPrecision, gridPageChartTimeMode, gridPageChartType, propertyType, referenceLineStyle, timeSeriesInterval, userScopeOperator } from 'shared/constants.json';
import { buildIconFromEntity } from 'shared/lib/icon';
import { canFillEmptyValues, chartRefKey, isBoundedProgress, isDateGrouping, isEqualGridPageTileChart, isProgressAggregation, seriesChartTypes, withBoundedProgress } from '@/composables/grid-page/chart/utils';
import { computed, inject, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { createPropsList } from '@/lib/props';
import { directProperties as directGoalActivityProperties } from '@/lib/goal-activity/properties';
import { directProperties as directUpdateProperties } from '@/lib/updates/properties';
import { getGradientPalette, mcColorSet } from '@/lib/charts/colors';
import { goalActivity as goalActivityConfig, goal as goalConfig, update as updateConfig } from 'shared/api/query/configs.json';
import { goalChartProperties } from '@/lib/goal/properties';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { normalizeTimeRangeDates } from '@/composables/saved-views/saved-view-cleaner';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const { t } = useI18n();
const store = useStore();
const isMobile = computed(() => store.state.breakpoint.smAndDown);

const { desktopSidebarHidden } = inject(SIDEBAR_SERVICE);

const props = defineProps({
  gridPageTile: {
    type: Object,
    required: true,
  },
  gridPage: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const breadcrumbItems = ref([
  {
    title: props.gridPage.title,
    icons: [{ value: buildIconFromEntity(props.gridPage) }],
    onClick: () => { emit('close'); },
  },
]);

const debounceSvc = useDebounce();
const gridPageService = useGridPage();

const editorRoot = ref(null);

// Cap the settings panel at half of the editor's container width so the chart
// area keeps at least 50% regardless of any outer sidebars. Falls back to the
// viewport before the root is mounted.
const resolveMaxWidth = () => {
  const containerWidth = editorRoot.value?.clientWidth ?? window.innerWidth;
  return Math.floor(containerWidth * 0.5);
};

const { value: settingsWidth, dragging: resizing, handleDragStartX: handleResizeStart } = useDragResize({
  min: 20 * 16,
  max: resolveMaxWidth,
  defaultValue: 350,
  reversed: true,
  storageKey: 'chart_editor_settings_width',
});

const getIconByType = (type) => {
  switch (type) {
    case gridPageChartType.bar:
      return 'horizontal-bar-chart';
    case gridPageChartType.column:
      return 'vertical-bar-chart';
    case gridPageChartType.line:
      return 'line-chart-colored';
    case gridPageChartType.pie:
      return 'pie-chart-colored';
    case gridPageChartType.metric:
      return 'metric-chart';
    case gridPageChartType.table:
      return 'chart-table';
    default:
      return '';
  }
};

function updateTitle(val) {
  debounceSvc.debounce(() => gridPageService.updateGridPageTile({ uid: props.gridPageTile.uid, title: val }), 500);
}

const descriptionEditMode = ref(false);
function updateDescription(val) {
  debounceSvc.debounce(() => gridPageService.updateGridPageTile({ uid: props.gridPageTile.uid, description: val }), 500);
}

const chartService = useGridPageTileChart();
const gridPageSvc = useGridPage();
const gridPageTileChart = computed(() => {
  const res = gridPageSvc.gridPageTileChart.value.find((e) => e.tile.uid === props.gridPageTile.uid);
  if (res === undefined) {
    return null;
  }

  return res;
});

const tablePosition = ref('center');

const snackbarService = useSnackbar();

const save = () => {
  chartService.updateSingle(toSave.value, { optimistic: false }).then(() => {
    snackbarService.success(t('success.saved'));
    init();
  });
};
const confirmDialog = useConfirmDialog();

const handleClose = () => {
  if (isDirty.value) {
    confirmDialog.confirm({
      title: t('gridPageChartEditor.closePrompt'),
      okText: t('general.yesDiscard'),
      okType: 'warning',
      cancelText: t('general.cancel'),
      onOk: () => {
        init();
        emit('close');
      },
    });
    return;
  }
  emit('close');
};

const onKeydown = (e) => {
  if (e.key === 'Escape') {
    handleClose();
  }
};
onMounted(() => { document.addEventListener('keydown', onKeydown); });
onUnmounted(() => { document.removeEventListener('keydown', onKeydown); });

const discard = () => {
  init();
};

const withClearedRefFields = (ref) => ({
  isProperty: false,
  ...(ref ?? {}),
});

const toSave = computed(() => ({
  uid: gridPageTileChart.value.uid,
  source: source.value,
  chartType: chartType.value,
  filter: filter.value.children.length > 0 ? filter.value : null,
  quickFilters: quickFilters.value,
  goalCycles: selectedCycles.value,
  groupBy: {
    uid: gridPageTileChart.value.groupBy.uid,
    ...withClearedRefFields(groupByToStore.value),
    customBucket: {
      uid: gridPageTileChart.value.groupBy.customBucket.uid,
      ...customBucket.value,
    },
  },
  aggregationConfig: {
    uid: aggregationConfig.value.uid,
    aggregation: {
      uid: aggregationConfig.value.aggregation.uid,
      ...withClearedRefFields(yAxisGroupBy.value),
    },
    stackOptions: {
      uid: aggregationConfig.value.stackOptions.uid,
      ...withClearedRefFields(stackOptionsToStore.value),
      customBucket: {
        uid: gridPageTileChart.value.aggregationConfig.stackOptions.customBucket.uid,
        ...stackCustomBucket.value,
      },
    },
    timeSeriesInterval: selectedInterval.value,
  },
  excludeNullValues: excludeNullValues.value,
  stacked: stacked.value,
  colorScheme: colorScheme.value,
  precision: precision.value,
  coloringMode: metricColoringMode.value,
  color: metricColor.value,
  metricTitle: metricTitle.value,
  metricTitleEnabled: metricTitleEnabled.value,
  coloringRules: [
    ...metricColoringRules.value.map((rule, index) => ({
      ...(rule.uid ? { uid: rule.uid } : {}),
      operator: rule.operator,
      value: rule.value,
      color: rule.color,
      sortOrder: index,
    })),
    ...removedColoringRules.value,
  ],
  referenceLines: [
    ...referenceLines.value.map((line) => ({ ...line, value: line.value !== null && line.value !== '' ? Number(line.value) : null })),
    ...removedReferenceLines.value,
  ],
}));

const stackOptionsToStore = computed(() => {
  if ([gridPageChartType.pie, gridPageChartType.metric].includes(chartType.value)) {
    return { groupBy: chartGroupByOption.none };
  }
  if (isTimeSeriesMode.value) {
    return { groupBy: chartGroupByOption.none };
  }
  return stackOptions.value;
});

const groupByToStore = computed(() => {
  if (chartType.value === gridPageChartType.metric) {
    return { groupBy: chartGroupByOption.none };
  }
  if (isTimeSeriesMode.value) {
    return { groupBy: chartGroupByOption.none };
  }
  return xAxisGroupBy.value;
});

const aggregationConfig = computed(() => gridPageTileChart.value.aggregationConfig);

const maxLength = (str, max) => {
  if (str.length === 0) {
    return t('general.untitled');
  }
  if (str.length > max) {
    return `${str.slice(0, max)}...`;
  }
  return str;
};

const source = ref(goalConfig.model);

const { loggedInUserAccount } = useLoggedInUserAccount();

const localEventBus = useEventBus();
provide(VIEW_EVENT_BUS, localEventBus);

const emptyFilter = () => ({
  account: { uid: loggedInUserAccount.value.uid },
  op: userScopeOperator.and,
  children: [],
});

const propertySvc = useProperties();
const goalsSvc = useGoals();
const userSvc = useUsers();
const spaceSvc = useSpaces();
const { resourceSettings } = useResourceSettings();
const paramEnricher = useParamEnricher(propertySvc, goalsSvc);
const paramDefaulter = useParamDefaulter();
const cleanFilterSvc = useCleanFilter(propertySvc, userSvc, spaceSvc);

const filter = ref(emptyFilter());
const quickFilters = ref([]);

const { properties: goalProperties, statusProperty } = useGoalProperty();
const { accountSettings } = useAccountSettings();
const canGroupByUser = computed(() => accountSettings.value?.canGroupByUser ?? false);

const selectedCycles = ref([]);

const chartTypes = [gridPageChartType.column, gridPageChartType.bar, gridPageChartType.line, gridPageChartType.pie, gridPageChartType.metric, gridPageChartType.table];
const chartType = ref(gridPageChartType.bar);

const xAxisGroupBy = ref(null);
const customBucket = ref(null);
const stackCustomBucket = ref(null);
const { availableRefs } = useChartProps(source);

const properties = computed(() => {
  if (source.value !== goalConfig.model || canGroupByUser.value) {
    return availableRefs.value;
  }
  // Goal-property charts only: account setting disables user-typed properties.
  return availableRefs.value.map((p) => {
    if (p.isProperty && p.property?.type === propertyType.user) {
      return { ...p, disabled: true, disabledReason: t('gridPageChartEditor.userPropertyDisabled') };
    }
    return p;
  });
});

const yAxisGroupBy = ref(null);
const stackOptions = ref(null);
const excludeNullValues = ref(false);
const stacked = ref(true);
const colorScheme = ref(gridPageChartColorScheme.automatic);
const precision = ref(gridPageChartPrecision.precisionAutomatic);
const metricColor = ref('default');
const metricColoringMode = ref(coloringMode.static);
const metricColoringRules = ref([]);
const removedColoringRules = ref([]);
const onUpdateColoringRules = (rules) => {
  const newUids = new Set(rules.filter((r) => r.uid).map((r) => r.uid));
  metricColoringRules.value
    .filter((r) => r.uid && !newUids.has(r.uid))
    .forEach((r) => removedColoringRules.value.push({ uid: r.uid, deletedAt: new Date() }));
  metricColoringRules.value = rules;
};
const metricColorLabel = computed(() => {
  if (metricColoringMode.value === coloringMode.dynamic) {
    return t('metricColorEditor.dynamicColor');
  }
  return t(`metricColorEditor.colors.${metricColor.value}`);
});
const referenceLines = ref([]);
const selectedInterval = ref(timeSeriesInterval.weekly);
const metricTitle = ref('');
const metricTitleEnabled = ref(false);
const updateMetricTitle = (val) => {
  metricTitle.value = val ?? '';
};
const toggleMetricTitleEnabled = () => {
  metricTitleEnabled.value = !metricTitleEnabled.value;
};

const SWATCH_COUNT = 5;
const colorSchemeOptions = [
  { text: t('gridPageChartEditor.colorSchemeAutomatic'), value: gridPageChartColorScheme.automatic, swatches: [] },
  { text: t('gridPageChartEditor.colorSchemeColorful'), value: gridPageChartColorScheme.colorful, swatches: mcColorSet.slice(0, SWATCH_COUNT) },
  ...[
    gridPageChartColorScheme.grey,
    gridPageChartColorScheme.brown,
    gridPageChartColorScheme.yellow,
    gridPageChartColorScheme.orange,
    gridPageChartColorScheme.red,
    gridPageChartColorScheme.pink,
    gridPageChartColorScheme.purple,
    gridPageChartColorScheme.blue,
    gridPageChartColorScheme.green,
  ].map((name) => ({
    text: t(`gridPageChartEditor.colorScheme${name.charAt(0).toUpperCase()}${name.slice(1)}`),
    value: name,
    swatches: getGradientPalette(name, SWATCH_COUNT),
  })),
];
const colorSchemeOptionsMap = Object.fromEntries(colorSchemeOptions.map((o) => [o.value, o]));

const showExcludeNullValues = computed(() => canFillEmptyValues(xAxisGroupBy.value));

const showPrecision = computed(() => {
  const agg = yAxisGroupBy.value?.aggregator;
  return agg !== aggregatorType.count && agg !== aggregatorType.countDistinct;
});

const getGroupByPropertyType = (groupBy) => (
  groupBy?.isProperty === true ? groupBy.property?.type : groupBy?.edgeType
);

const isDateAxis = (groupByValue) => {
  if (isNullOrUndefined(groupByValue)) {
    return false;
  }
  if (isDateGrouping(groupByValue.groupBy)) {
    return true;
  }
  if (groupByValue.isProperty === false && groupByValue.edge === goalConfig.edges.goalCycle) {
    return true;
  }
  return getGroupByPropertyType(groupByValue) === propertyType.date;
};

const manualSortOption = { text: t('gridPageChartEditor.sortManual'), value: chartSortBy.manual };

const dateSortOptions = [
  { text: t('gridPageChartEditor.sortOldToNew'), value: chartSortBy.valueAsc },
  { text: t('gridPageChartEditor.sortNewToOld'), value: chartSortBy.valueDesc },
];

const defaultSortOptions = [
  { text: t('gridPageChartEditor.sortAscending'), value: chartSortBy.valueAsc },
  { text: t('gridPageChartEditor.sortDescending'), value: chartSortBy.valueDesc },
  { text: t('gridPageChartEditor.sortCountAscending'), value: chartSortBy.countAsc },
  { text: t('gridPageChartEditor.sortCountDescending'), value: chartSortBy.countDesc },
  manualSortOption,
];

const valueSortOptions = [
  { text: t('gridPageChartEditor.sortAscending'), value: chartSortBy.valueAsc },
  { text: t('gridPageChartEditor.sortDescending'), value: chartSortBy.valueDesc },
  manualSortOption,
];

const isBucketAxis = (groupByValue) => groupByValue?.groupBy === chartGroupByOption.bucket;
const withoutManual = (options) => options.filter((o) => o.value !== chartSortBy.manual);

const xAxisSortOptions = computed(() => {
  if (isDateAxis(xAxisGroupBy.value)) return dateSortOptions;
  if (isBucketAxis(xAxisGroupBy.value)) return withoutManual(defaultSortOptions);
  return defaultSortOptions;
});
const stackSortOptions = computed(() => {
  if (isDateAxis(stackOptions.value)) return dateSortOptions;
  if (isBucketAxis(stackOptions.value)) return withoutManual(valueSortOptions);
  return valueSortOptions;
});

const { defaultSortByValue } = useChartSort();
const defaultSortBy = (groupBy) => groupBy?.sortBy ?? defaultSortByValue(groupBy);

const toUidStrings = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.map((o) => String(o?.uid ?? o));
};

const settingsOptionOrderUids = (groupBy) => {
  if (!groupBy) return null;

  // Space grouping (via property or edge) — ordered by workspace spaceOrder.
  const isSpaceEdge = groupBy.isProperty === false && groupBy.edgeType === propertyType.space;
  if (isSpaceEdge) {
    return toUidStrings(resourceSettings.value?.spaceOrder);
  }

  if (!groupBy.isProperty) return null;

  const propRef = groupBy.property;
  if (!propRef?.uid) return null;
  // The persisted chart only stores `{uid}` on groupBy.property; the full
  // entity (type, propertyOptionOrder, etc.) lives in goalProperties.
  const prop = goalProperties.value.find((p) => p.uid === propRef.uid) ?? propRef;

  if (prop.type === propertyType.space) {
    return toUidStrings(resourceSettings.value?.spaceOrder);
  }

  const ordered = [propertyType.status, propertyType.options, propertyType.singleSelect];
  if (!ordered.includes(prop.type)) return null;
  return toUidStrings(prop.propertyOptionOrder);
};

const seedManualOrder = (groups, groupBy) => {
  const uids = groups.map((g) => String(g.originalValue));
  const settingsOrder = settingsOptionOrderUids(groupBy);
  if (!settingsOrder) return uids;

  const orderIndex = new Map(settingsOrder.map((uid, i) => [uid, i]));
  return [...uids].sort((a, b) => {
    const ia = orderIndex.has(a) ? orderIndex.get(a) : Number.MAX_SAFE_INTEGER;
    const ib = orderIndex.has(b) ? orderIndex.get(b) : Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
};

const makeSortBy = (groupByRef, fallbackGroupsKey) => computed({
  get: () => defaultSortBy(groupByRef.value),
  set: (val) => {
    const update = { ...groupByRef.value, sortBy: val };
    if (val === chartSortBy.manual && (!groupByRef.value.manualOrder || groupByRef.value.manualOrder.length === 0)) {
      const groups = chartDataQuery.result.value?.[fallbackGroupsKey];
      if (groups) {
        update.manualOrder = seedManualOrder(groups, groupByRef.value);
      }
    }
    groupByRef.value = update;
  },
});

const makeGroupByField = (groupByRef, field) => computed({
  get: () => groupByRef.value?.[field] ?? [],
  set: (val) => { groupByRef.value = { ...groupByRef.value, [field]: val }; },
});

const xAxisSortBy = makeSortBy(xAxisGroupBy, 'filteredCategories');
const stackSortBy = makeSortBy(stackOptions, 'filteredStackedGroups');

const xAxisManualOrder = makeGroupByField(xAxisGroupBy, 'manualOrder');
const xAxisHiddenGroups = makeGroupByField(xAxisGroupBy, 'hiddenGroups');
const stackManualOrder = makeGroupByField(stackOptions, 'manualOrder');
const stackHiddenGroups = makeGroupByField(stackOptions, 'hiddenGroups');

const xAxisGroups = computed(() => chartDataQuery.result.value?.allCategories ?? chartDataQuery.result.value?.filteredCategories ?? []);
const stackGroups = computed(() => chartDataQuery.result.value?.allStackedGroups ?? chartDataQuery.result.value?.filteredStackedGroups ?? []);

const showXAxisSort = computed(() => xAxisGroupBy.value?.groupBy !== chartGroupByOption.none && !isTimeSeriesMode.value);
const showStackSort = computed(() => stackOptions.value?.groupBy !== chartGroupByOption.none && !isTimeSeriesMode.value);

const timeMode = ref(gridPageChartTimeMode.snapshot);

const isTimeSeriesMode = computed(() => timeMode.value === gridPageChartTimeMode.overTime);

const timeSeriesChartTypesByAggregator = { [aggregatorType.statusDistributionOverTime]: [gridPageChartType.line, gridPageChartType.column] };

const visibleChartTypes = computed(() => {
  if (!isTimeSeriesMode.value) {
    return chartTypes;
  }
  return timeSeriesChartTypesByAggregator[yAxisGroupBy.value?.aggregator] ?? [gridPageChartType.line];
});

const categoryIsXAxis = computed(() => chartType.value !== gridPageChartType.bar);

const timeModeOptions = [
  { text: t('gridPageChartEditor.snapshot'), value: gridPageChartTimeMode.snapshot },
  { text: t('gridPageChartEditor.overTime'), value: gridPageChartTimeMode.overTime },
];

const timeSeriesIntervalOptions = [
  { text: t('gridPageChartEditor.daily'), value: timeSeriesInterval.daily },
  { text: t('gridPageChartEditor.weekly'), value: timeSeriesInterval.weekly },
  { text: t('gridPageChartEditor.monthly'), value: timeSeriesInterval.monthly },
];

const toggleExcludeNullValues = () => {
  excludeNullValues.value = !excludeNullValues.value;
};

const showBoundedProgress = computed(() => isProgressAggregation(yAxisGroupBy.value));
const boundedProgress = computed(() => isBoundedProgress(yAxisGroupBy.value));

const toggleBoundedProgress = () => {
  yAxisGroupBy.value = withBoundedProgress(yAxisGroupBy.value, !boundedProgress.value);
};

const toggleStacked = () => {
  stacked.value = !stacked.value;
};
const showStacked = computed(() => seriesChartTypes.includes(chartType.value)
  && !isTimeSeriesMode.value
  && stackOptions.value?.groupBy !== chartGroupByOption.none);

const showReferenceLines = computed(() => {
  if (isTimeSeriesMode.value) {
    return [aggregatorType.progressOverTime, aggregatorType.progressOverTimeUnbounded, aggregatorType.statusScoreOverTime]
      .includes(yAxisGroupBy.value?.aggregator);
  }
  return [gridPageChartType.bar, gridPageChartType.column, gridPageChartType.line].includes(chartType.value);
});

const referenceLineStyleOptions = [
  { text: t('gridPageChartEditor.referenceLine.solid'), value: referenceLineStyle.solid },
  { text: t('gridPageChartEditor.referenceLine.dash'), value: referenceLineStyle.dash },
  { text: t('gridPageChartEditor.referenceLine.dot'), value: referenceLineStyle.dot },
];

const addReferenceLine = () => {
  referenceLines.value.push({ value: null, label: '', style: referenceLineStyle.solid, color: 'grey' });
};

const removedReferenceLines = ref([]);

const removeReferenceLine = (index) => {
  const removed = referenceLines.value.splice(index, 1)[0];
  if (removed.uid) {
    removedReferenceLines.value.push({ uid: removed.uid, deletedAt: new Date() });
  }
};

const { userLang } = useLoggedInUser();
const statusPropertyLabel = computed(() => (statusProperty.value ? textByLang(statusProperty.value.label, userLang.value) : 'Status'));

const timeSeriesMetricOptions = computed(() => [
  { key: aggregatorType.progressOverTime, text: t('gridPageChartEditor.progressOverTime'), icon: 'line-chart' },
  { key: aggregatorType.statusScoreOverTime, text: t('gridPageChartEditor.statusScore', { property: statusPropertyLabel.value }), icon: 'status' },
  { key: aggregatorType.statusDistributionOverTime, text: t('gridPageChartEditor.statusDistribution', { property: statusPropertyLabel.value }), icon: 'status' },
]);

const timeSeriesMetricOptionsByKey = computed(() => Object.fromEntries(timeSeriesMetricOptions.value.map((o) => [o.key, o])));

const selectedTimeSeriesMetricKey = computed(() => (
  yAxisGroupBy.value?.aggregator === aggregatorType.progressOverTimeUnbounded
    ? aggregatorType.progressOverTime
    : yAxisGroupBy.value?.aggregator
));

const goalDefaultProps = computed(() => createPropsList({
  properties: goalProperties.value,
  directProperties: goalChartProperties(t),
  userLang: userLang.value,
  model: goalConfig.model,
}));

const updateDefaultProps = computed(() => createPropsList({
  properties: [],
  directProperties: directUpdateProperties(t),
  userLang: userLang.value,
  model: updateConfig.model,
}));

const goalActivityDefaultProps = computed(() => createPropsList({
  properties: [],
  directProperties: directGoalActivityProperties(t),
  userLang: userLang.value,
  model: goalActivityConfig.model,
}));

const propsForSource = (sourceModel) => {
  switch (sourceModel) {
    case goalConfig.model:
      return goalDefaultProps.value;
    case updateConfig.model:
      return updateDefaultProps.value;
    case goalActivityConfig.model:
      return goalActivityDefaultProps.value;
    default:
      return [];
  }
};

const filterProps = computed(() => propsForSource(source.value));

const enrichAndCleanFilter = (rawFilter, sourceModel) => {
  if (rawFilter === null || rawFilter === undefined) {
    return null;
  }
  // Restore scope arrays stripped by backend `omitempty` (pkg/xfmr/document/view.go).
  const enriched = paramDefaulter.setUserScopeTreeDefaults(paramEnricher.enrich(copy(rawFilter)));
  const timeNormalized = normalizeTimeRangeDates(enriched);
  const cleaned = cleanFilterSvc.cleanFilter({
    filter: timeNormalized,
    props: propsForSource(sourceModel),
  });
  if (cleaned === null || cleaned.children.length === 0) {
    return null;
  }
  return cleaned;
};

const cleanedSavedFilter = computed(() => enrichAndCleanFilter(
  gridPageTileChart.value.filter,
  gridPageTileChart.value.source,
));

const prunedSavedQuickFilters = computed(() => {
  const keys = gridPageTileChart.value.quickFilters ?? [];
  if (cleanedSavedFilter.value === null) {
    return [];
  }
  const childKeys = new Set(cleanedSavedFilter.value.children.map((c) => c.key));
  return keys.filter((k) => childKeys.has(k));
});

const sources = [
  {
    text: t('gridPageChartEditor.goal'),
    icon: 'compass',
    value: goalConfig.model,
  },
  {
    text: t('gridPageChartEditor.checkin'),
    icon: 'history',
    value: updateConfig.model,
  },
  {
    text: t('gridPageChartEditor.goalActivity'),
    icon: 'sync',
    value: goalActivityConfig.model,
  },
];

const sourceDisplayValue = computed(() => sources.find((s) => s.value === source.value)?.text);
const intervalDisplayValue = computed(() => timeSeriesIntervalOptions.find((o) => o.value === selectedInterval.value)?.text);
const timeSeriesMetricDisplayValue = computed(() => timeSeriesMetricOptionsByKey.value[selectedTimeSeriesMetricKey.value]?.text);

const init = () => {
  source.value = gridPageTileChart.value.source;
  chartType.value = gridPageTileChart.value.chartType;
  filter.value = cleanedSavedFilter.value !== null ? copy(cleanedSavedFilter.value) : emptyFilter();
  quickFilters.value = [...prunedSavedQuickFilters.value];
  selectedCycles.value = gridPageTileChart.value.goalCycles;
  yAxisGroupBy.value = gridPageTileChart.value.aggregationConfig.aggregation;
  stackOptions.value = { ...gridPageTileChart.value.aggregationConfig.stackOptions, customBucket: undefined };
  xAxisGroupBy.value = { ...gridPageTileChart.value.groupBy, customBucket: undefined };
  customBucket.value = { ...gridPageTileChart.value.groupBy.customBucket };
  stackCustomBucket.value = { ...gridPageTileChart.value.aggregationConfig.stackOptions.customBucket };
  excludeNullValues.value = gridPageTileChart.value.excludeNullValues ?? false;
  stacked.value = gridPageTileChart.value.stacked ?? true;
  colorScheme.value = gridPageTileChart.value.colorScheme ?? gridPageChartColorScheme.automatic;
  precision.value = gridPageTileChart.value.precision ?? gridPageChartPrecision.precisionAutomatic;
  metricColor.value = gridPageTileChart.value.color ?? 'default';
  metricColoringMode.value = gridPageTileChart.value.coloringMode ?? coloringMode.static;
  metricColoringRules.value = [...(gridPageTileChart.value.coloringRules ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  removedColoringRules.value = [];
  referenceLines.value = (gridPageTileChart.value.referenceLines ?? []).map((line) => ({ ...line }));
  removedReferenceLines.value = [];
  selectedInterval.value = gridPageTileChart.value.aggregationConfig.timeSeriesInterval ?? timeSeriesInterval.weekly;
  metricTitle.value = gridPageTileChart.value.metricTitle ?? '';
  metricTitleEnabled.value = gridPageTileChart.value.metricTitleEnabled ?? false;
  timeMode.value = TIME_SERIES_AGGREGATORS.includes(gridPageTileChart.value.aggregationConfig.aggregation.aggregator)
    ? gridPageChartTimeMode.overTime
    : gridPageChartTimeMode.snapshot;
  if (!visibleChartTypes.value.includes(chartType.value)) {
    chartType.value = gridPageChartType.line;
  }
};

const chartForQuery = ref(null);
watch(toSave, (val) => {
  const { referenceLines: _referenceLines, ...rest } = val;
  if (JSON.stringify(rest) !== JSON.stringify(chartForQuery.value)) {
    chartForQuery.value = rest;
  }
}, { immediate: true, deep: true });

const chartDataQuery = useChartDataQuery(chartForQuery);

watch(() => gridPageTileChart.value?.filter, (rawFilter) => {
  if (rawFilter === null || rawFilter === undefined) {
    return;
  }
  paramEnricher.fetchGoals(paramEnricher.goalRelations(rawFilter));
}, { immediate: true });

init();

const findMatchingProperty = (currentValue, availableProperties) => {
  if (currentValue === null || currentValue === undefined) {
    return null;
  }
  return availableProperties.find((prop) => {
    if (currentValue.isProperty === true && prop.isProperty === true) {
      return currentValue.property?.uid === prop.property?.uid;
    }
    if (currentValue.isProperty === false && prop.isProperty === false) {
      return currentValue.edge === prop.edge;
    }
    return false;
  });
};

const getDefaultGroupBy = (availableProperties) => {
  const nonDateNumberProp = availableProperties.find((p) => {
    const type = p.isProperty === true ? p.property.type : p.edgeType;
    return type !== propertyType.date && type !== propertyType.number;
  });
  if (nonDateNumberProp !== undefined) {
    return { ...nonDateNumberProp, groupBy: chartGroupByOption.unique };
  }
  const firstProp = availableProperties[0];
  if (firstProp !== undefined) {
    const type = firstProp.isProperty === true ? firstProp.property.type : firstProp.edgeType;
    const groupBy = type === propertyType.date ? chartGroupByOption.day : chartGroupByOption.unique;
    return { ...firstProp, groupBy };
  }
  return { groupBy: chartGroupByOption.none };
};

const isSameAxisProperty = (a, b) => {
  if (a == null || b == null) {
    return false;
  }
  if (a.isProperty !== b.isProperty) {
    return false;
  }
  return chartRefKey(a) === chartRefKey(b);
};

const setXAxisGroupBy = (val) => {
  xAxisGroupBy.value = val;
  if (isSameAxisProperty(val, yAxisGroupBy.value)) {
    yAxisGroupBy.value = { aggregator: aggregatorType.count };
  }
};

// The pickers offer a single "Progress" ref, on the uncapped edge; which of the
// two progress edges is actually stored is decided by the cap switch, so a
// progress aggregation coming out of a picker inherits the switch and defaults
// to capped.
const setYAxisAggregation = (val) => {
  if (!isProgressAggregation(val)) {
    yAxisGroupBy.value = val;
    return;
  }
  const bounded = isProgressAggregation(yAxisGroupBy.value) ? boundedProgress.value : true;
  yAxisGroupBy.value = withBoundedProgress(val, bounded);
};

const setYAxisGroupBy = (val) => {
  setYAxisAggregation(val);
  if (isSameAxisProperty(val, xAxisGroupBy.value)) {
    const alternatives = properties.value.filter((p) => !isSameAxisProperty(p, val));
    if (alternatives.length > 0) {
      xAxisGroupBy.value = getDefaultGroupBy(alternatives);
    }
  }
};

const selectTimeSeriesMetric = (key) => {
  if (key === selectedTimeSeriesMetricKey.value) {
    return;
  }
  yAxisGroupBy.value = { aggregator: key };
};

watch(source, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }
  filter.value = emptyFilter();
  quickFilters.value = [];

  if (newVal !== goalConfig.model && isTimeSeriesMode.value) {
    timeMode.value = gridPageChartTimeMode.snapshot;
    yAxisGroupBy.value = { aggregator: aggregatorType.count };
  }

  // properties.value is already updated since source changed
  const newProperties = properties.value;

  const matchingXAxis = findMatchingProperty(xAxisGroupBy.value, newProperties);
  xAxisGroupBy.value = matchingXAxis !== null && matchingXAxis !== undefined
    ? { ...matchingXAxis, groupBy: xAxisGroupBy.value.groupBy }
    : getDefaultGroupBy(newProperties);

  const matchingYAxis = findMatchingProperty(yAxisGroupBy.value, newProperties);
  yAxisGroupBy.value = matchingYAxis !== null && matchingYAxis !== undefined
    ? { ...matchingYAxis, aggregator: yAxisGroupBy.value.aggregator }
    : { aggregator: aggregatorType.count };

  const matchingStack = findMatchingProperty(stackOptions.value, newProperties);
  stackOptions.value = matchingStack !== null && matchingStack !== undefined
    ? { ...matchingStack, groupBy: stackOptions.value.groupBy }
    : { groupBy: chartGroupByOption.none };
});

watch(chartType, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }
  // Pie and metric have no time-series variant, so selecting them while in
  // time-series mode must exit it — otherwise they would not be in
  // `visibleChartTypes` and the selection would be reverted.
  if ([gridPageChartType.pie, gridPageChartType.metric].includes(newVal) && isTimeSeriesMode.value) {
    timeMode.value = gridPageChartTimeMode.snapshot;
  }
});

watch(timeMode, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }
  if (newVal === gridPageChartTimeMode.overTime) {
    if (source.value !== goalConfig.model) {
      source.value = goalConfig.model;
    }
    if (!TIME_SERIES_AGGREGATORS.includes(yAxisGroupBy.value?.aggregator)) {
      yAxisGroupBy.value = { aggregator: aggregatorType.progressOverTime };
    }
    if (!visibleChartTypes.value.includes(chartType.value)) {
      chartType.value = gridPageChartType.line;
    }
  } else if (TIME_SERIES_AGGREGATORS.includes(yAxisGroupBy.value?.aggregator)) {
    yAxisGroupBy.value = { aggregator: aggregatorType.count };
  }
});

watch(() => yAxisGroupBy.value?.aggregator, () => {
  if (!isTimeSeriesMode.value) {
    return;
  }
  if (!visibleChartTypes.value.includes(chartType.value)) {
    chartType.value = gridPageChartType.line;
  }
});

const isDirty = computed(() => !isEqualGridPageTileChart(
  toSave.value,
  {
    ...gridPageTileChart.value,
    filter: cleanedSavedFilter.value,
    quickFilters: prunedSavedQuickFilters.value,
  },
));

watch(gridPageTileChart, () => {
  if (!isDirty.value) {
    init();
  }
});
</script>

<style scoped lang="scss">
@import "shared/assets/scss/box-shadow";

$top-bar-height: 4.6rem;

.grid-page-chart-editor {
  display: grid;
  grid-template-rows: $top-bar-height 1fr;
  height: var(--viewport-height-100);
  overflow: hidden;
  position: relative;

  ._controls {
    position: fixed;
    bottom: 2rem;
    right: 2rem;

    @media (max-width: $screen-size-md) {
      bottom: 6rem;
      right: 1rem;
    }

    ._feedback-btn {
      border-radius: $btn-border-radius;
      background: white;
      margin-left: .6rem;

      @include box_shadow(1);
    }
  }

  ._top {
    flex: 0 0 4rem;
    border-bottom: 1px solid $border-color;
    display: flex;
    align-items: center;
    padding: 0 2rem;

    &.-sidebar-hidden {
      padding-left: 5.4rem;
    }

    ._left {
      display: flex;
      overflow: hidden;

      ._divider {
        margin: 0 .6rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    ._right {
      margin-left: auto;
      display: flex;
      gap: .4rem;
      flex-shrink: 0;
    }

    @media (max-width: $screen-size-md) {
      padding: 0 1rem;

      &.-sidebar-hidden {
        padding-left: 1rem;
      }

      ._left {
        min-width: 0;

        ._breadcrumbs,
        ._divider,
        ._description {
          display: none;
        }

        ._title {
          font-weight: $font-weight-semibold;
        }
      }

      ._right {
        ._discard-btn {
          display: none;
        }
      }
    }
  }

  ._bottom {
    display: grid;
    grid-template-columns: 1fr auto;
    height: 100%;
    overflow: hidden;

    ._left {
      display: grid;
      overflow: hidden;
      position: relative;

      ._loading-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: white;
        z-index: 2;
      }

      ._draggable-layout {
        &.-table {
          display: grid;
          grid-template-rows: auto 1fr;
          min-height: 0;
          overflow: auto;
        }
      }

      ._chart-container {
        height: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        ._chart-content {
          flex: 1;
          min-height: 0;
          display: flex;
          overflow: hidden;
        }

        &.-table {
          overflow: auto;

          ._chart-content {
            display: inline-table;
          }
        }
      }

      ._data-limit-hint {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
      }

      ._table-container {
        display: inline-table;
        min-width: 100%;
        padding-top: 2rem;
      }
    }

    ._right {
      position: relative;
      border-left: 1px solid $border-color;
      overflow-y: auto;
      flex-shrink: 0;

      ._resize-handle {
        cursor: col-resize;
        width: 2rem;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        z-index: 1;
        box-shadow: inset 2px 0 0 transparent;
        transition: box-shadow 0s 0s;

        &:hover {
          transition-delay: 300ms;
        }

        &:hover, &.-dragging {
          box-shadow: inset 2px 0 0 $primary-color;
        }

        &.-dragging {
          transition-delay: 0s;
        }
      }

      ._inner {
        padding: 1.2rem 0.8rem 6rem;
      }

      ._metric-title-input-row {
        padding: 0.4rem 1.2rem 0.8rem;
      }
    }

    ._time-mode-tabs {
      display: flex;
      border-bottom: 1px solid $border-color;

      ._time-mode-tab {
        flex: 1;
        display: flex;

        .m-tab {
          flex: 1;
          justify-content: center;
        }

        &.-disabled {
          cursor: not-allowed;

          .m-tab {
            opacity: .4;
            pointer-events: none;
          }
        }
      }
    }

    @media (max-width: $screen-size-md) {
      grid-template-columns: 1fr;

      ._left {
        display: none;
      }

      ._right {
        border-left: none;

        ._resize-handle {
          display: none;
        }
      }
    }
  }

  ._section-header {
    font-size: $font-size-2;
    font-weight: $font-weight-semibold;
    color: $font-color-secondary;
    padding: 2rem 0 0.2rem 1.2rem;

    &:first-child {
      padding-top: 0;
    }
  }

  ._chart-type-selector {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 0.6rem 0.4rem 0.4rem 1.2rem;

    ._chart-btn {
      border: none;
      width: 4.2rem;
      height: 4.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 0 1px $input-border-color;

      ._icon-wrapper {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.-selected {
        box-shadow: 0 0 0 2px $primary-color;
      }

      &.-disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
}

._color-scheme-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: .8rem;

  ._color-scheme-label {
    flex: 1;
    white-space: nowrap;
  }

  ._color-scheme-swatches {
    display: flex;
    gap: .3rem;
    flex-shrink: 0;

    ._color-swatch {
      width: 1.6rem;
      height: 1.6rem;
      border-radius: .3rem;
    }
  }
}
</style>
